<template>
    <ul class="navigation">
        <router-link tag="li" active-class="navigation__active" :to="{name: 'dashboard'}">
            <a><i class="zmdi zmdi-home"></i> Home</a>
        </router-link>

        <li class="navigation__sub" :class="{'navigation__sub--active': isActiveItem('/blocks')}">
            <b-link @click="toggleBlocks">
                <i class="zmdi zmdi-view-week"></i> My Blocks
                <b-dropdown right variant="outline-secondary" class="nav-action" no-caret
                            v-b-tooltip.right="'Establish block'" v-if="sortedLinkedCropTypes.length > 0">
                    <template slot="button-content">
                        <i class="zmdi zmdi-more-vert"></i>
                    </template>
                    <b-dropdown-item :to="establishBlockLink(cropType)" v-for="cropType in sortedLinkedCropTypes" :key="cropType.id">
                        <i class="zmdi zmdi-plus text-success"></i>
                        Establish {{ cropType.name.toLowerCase() }} block
                    </b-dropdown-item>
                </b-dropdown>
            </b-link>

            <keep-alive>
                <ul v-if="showBlocks" class="animated">
                    <li class="navigation__sub">
                        <router-link class="navigation__heading" active-class="navigation__active"
                                     :to="{name: 'blocks.browse'}" exact>
                            All Blocks
                        </router-link>
                        <ul>
                            <router-link tag="li" active-class="navigation__active" :to="{name: 'spray-diary.view-by-date', params: {startRange: lastYearRange}}">
                                <a>This time last year</a>
                            </router-link>
                            <router-link tag="li" active-class="navigation__active" :to="{name: 'spray-diary.create-bulk'}">
                                <a>Bulk spray diary update</a>
                            </router-link>

                            <router-link tag="li" active-class="navigation__active" :to="{name: 'weed-spray-diary.browse'}">
                                <a>Weed Spray diary</a>
                            </router-link>
                            <router-link tag="li" active-class="navigation__active" :to="{name: 'base-fertiliser.view'}">
                                <a>Base Fertiliser</a>
                            </router-link>
                        </ul>
                    </li>

                    <li class="navigation__sub" v-for="cropType in sortedLinkedCropTypes" :key="cropType.id">
                        <router-link class="navigation__heading" active-class="navigation__active"
                                     :to="{name: 'blocks.crop-type.browse', params: {cropTypeId: cropType.id}}" exact>
                            {{ cropType.name }} Blocks ({{ cropType.blocks_count | number_format }})
                        </router-link>
                        <ul>
                            <router-link tag="li" active-class="navigation__active"
                                         :to="{name: 'blocks.crop-type.spray-diary.create', params: {cropTypeId: cropType.id}}">
                                <a>Bulk spray diary update</a>
                            </router-link>
                        </ul>
                    </li>
                </ul>
            </keep-alive>
        </li>

        <li class="navigation__sub" :class="{'navigation__sub--active': isActiveItem('/stock-management')}">
            <b-link @click="toggleStockManagement">
                <i class="zmdi zmdi-truck"></i> Stock Management
            </b-link>

            <keep-alive>
                <ul v-if="showStockManagement" class="animated">
                    <router-link tag="li" active-class="navigation__active" :to="{name: 'stock-management', params: {view: 'chemicals'}}">
                        <a>Chemicals In Use</a>
                    </router-link>
                    <router-link tag="li" active-class="navigation__active" :to="{name: 'stock-management', params: {view: 'fertilisers'}}">
                        <a>Fertilisers In Use</a>
                    </router-link>
                    <router-link tag="li" active-class="navigation__active" :to="{name: 'stock-management'}" exact>
                        <a>Stock on Hand</a>
                    </router-link>
                    <router-link tag="li" active-class="navigation__active" :to="{name: 'stock-management', params: {view: 'purchase-input'}}">
                        <a>Purchase Input</a>
                    </router-link>
                    <router-link tag="li" active-class="navigation__active" :to="{name: 'stock-management', params: {view: 'purchase-history'}}">
                        <a>Purchase History</a>
                    </router-link>
                </ul>
            </keep-alive>
        </li>


        <li class="navigation__sub" :class="{'navigation__sub--active': isActiveItem('/setup')}">
            <b-link @click="toggleSetup">
                <i class="zmdi zmdi-settings"></i> Setup
            </b-link>

            <keep-alive>
                <ul v-if="showSetup" class="animated">
                    <router-link tag="li" active-class="navigation__active"
                                 :to="{name: 'setup'}" exact>
                        <a>Account</a>
                    </router-link>
                    <router-link tag="li" active-class="navigation__active" :to="{name: 'setup', params: {view: 'packages'}}">
                        <a>Packages</a>
                    </router-link>
                    <router-link tag="li" active-class="navigation__active" :to="{name: 'setup', params: {view: 'crop-types'}}">
                        <a>Crop Types</a>
                    </router-link>
                    <router-link tag="li" active-class="navigation__active" :to="{name: 'setup', params: {view: 'contractors'}}">
                        <a>Contractors</a>
                    </router-link>
                    <router-link tag="li" active-class="navigation__active"
                                 v-if="!user.is_processor_managed"
                                 :to="{name: 'setup', params: {view: 'processors'}}">
                        <a>Processors</a>
                    </router-link>
                    <router-link tag="li" active-class="navigation__active" :to="{name: 'setup', params: {view: 'growsafe'}}">
                        <a>Growsafe Certificates</a>
                    </router-link>
                    <router-link tag="li" active-class="navigation__active" :to="{name: 'setup', params: {view: 'equipment'}}">
                        <a>Equipment</a>
                    </router-link>
                    <router-link tag="li" active-class="navigation__active" :to="{name: 'setup', params: {view: 'orchard-maps'}}">
                        <a>Orchard Maps</a>
                    </router-link>
                </ul>
            </keep-alive>
        </li>
    </ul>
</template>

<script>
import listLinkedCropTypesQuery from '@components/Sidebars/Grower/queryListGrowerLinkedCropTypes.graphql';
import {subMonths, format as dateFormat} from 'date-fns';

export default {
  name: 'GrowerAppSidebar',
  props: ['user'],
  apollo: {
    linkedCropTypes: {
      query: listLinkedCropTypesQuery,
      update: data => data.filterGrowerCropTypeLinks,
    },
  },
  data() {
    return {
      linkedCropTypes: [],
      showBlocks: this.isActiveItem('/blocks'),
      showSetup: this.isActiveItem('/setup'),
      showStockManagement: this.isActiveItem('/stock-management'),
      loading: 0,
    };
  },
  computed: {
    sortedLinkedCropTypes() {
      return [...this.linkedCropTypes].sort(
        ({ name: a }, { name: b }) => (a < b ? -1 : a > b ? 1 : 0),
      );
    },
    lastYearRange(){
      const end = subMonths(new Date(), 9);
      const start = subMonths(end, 3);
      return `${dateFormat(start, 'YYYY-MM-DD')} to ${dateFormat(end, 'YYYY-MM-DD')}`;
    }
  },
  methods: {
    establishBlockLink(cropType) {
      return {
        name: 'blocks.create',
        params: { cropTypeId: cropType.id },
      };
    },
    updateBlockLink(block) {
      return { name: 'blocks.update', params: { blockId: block.id } };
    },
    isActiveItem(url) {
      return this.$route.path.startsWith(url);
    },
    toggleBlocks() {
      this.showBlocks = !this.showBlocks;
    },
    toggleSetup() {
      this.showSetup = !this.showSetup;
    },
    toggleStockManagement() {
      this.showStockManagement = !this.showStockManagement;
    },
  },
};
</script>
